import React, { Component } from 'react';
import { Frame, GetSDK, sdkKey } from '@mp/common';
import { Game, Types, Events } from 'phaser';
import { WelcomeScene } from '../scenes/WelcomeScene';
import { GameScene } from '../scenes/GameScene';
import { SceneIds } from '../scenes/SceneIds';
import { ISceneConfig } from '../interfaces';
import { SceneTransitions } from '../scenes/SceneTransitions';
import { WelcomeView } from './WelcomeView';
import { SceneEvents } from '../scenes/SceneEvents';
import { GameView } from './GameView';
import { GameOverView } from './GameOverView';
import { MakeGameState } from '../GameState';
import { GameOverScene } from '../scenes/GameOverScene';
import { portalSelectType, createPortalSelectClosure } from '../scene-components/PortalSelect';
import { portalSelectorSphereType, createPortalSelectorClosure } from '../scene-components/PortalSelectorSphere';
import portals from '../data/portals_khufu_ko.json';

import { overlaySelectType, createOverlaySelectClosure } from '../scene-components/OverlaySelect';
import { overlaySelectorSphereType, createOverlaySelectorClosure } from '../scene-components/OverlaySelector';
import overlays from '../data/overlays_khufu_ko.json';
import settings from '../data/settings_khufu_ko.json';

export const ModelSid = settings.sid;

// Vendor specific full screen functions
declare global {
  interface Document {
    readonly fullscreenElement?: Element;
    readonly mozFullScreenElement?: Element;
    readonly webkitFullscreenElement?: Element;
    readonly msFullscreenElement?: Element;

    readonly fullscreenEnabled: boolean;
    readonly mozFullScreenEnabled?: boolean;
    readonly webkitFullscreenEnabled?: boolean;
    readonly msFullscreenEnabled?: boolean;

    mozCancelFullScreen?(): void;
    webkitExitFullscreen?(): void;
    msExitFullscreen?(): void;
  }
  interface Element {
    mozRequestFullScreen?(): void;
    webkitRequestFullscreen?(): void;
    msRequestFullscreen?(): void;
  }

}

interface State {
  currentScene: SceneIds;
  sceneConfig: ISceneConfig|null;
}

export class MainView extends Component<{}, State> {
  private sdk: any = null;
  private nodes: any = null;
  private paths: any = null;
  private overlay_nodes: any = null;
  private gltf_nodes: any = null;
  private game: Game = null;
  // hard coding the total number of portals to win
  private winningCaptureTotal = 5;
  private rootRef: React.RefObject<HTMLDivElement>;
  private queryString: string = '';
  private sdkKey: string = sdkKey;

  constructor(props: {}) {
    super(props);
    this.rootRef = React.createRef<HTMLDivElement>();

    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('m')) {
      urlParams.set('m', ModelSid);
    }
    // ensure applicationKey is inserted into the bundle query string
    if (urlParams.has('applicationKey')) {
      this.sdkKey = urlParams.get('applicationKey');
    }
    else {
      urlParams.set('applicationKey', this.sdkKey);
    }

    this.queryString = urlParams.toString();
    this.state = {
      currentScene: SceneIds.Welcome,
      sceneConfig: null,
    }
  }

  async componentDidMount() {
    this.sdk = await GetSDK('sdk-iframe', this.sdkKey);

    const iframeElement = document.getElementById('sdk-iframe') as HTMLIFrameElement;
    const stylesheet = document.createElement("link");
    stylesheet.rel = 'stylesheet';
    stylesheet.type = 'text/css';
    stylesheet.href = '../assets/showcase.css';
    iframeElement.contentDocument.getElementsByTagName('head')[0].appendChild(stylesheet)

    let phaserCanvas: HTMLCanvasElement = document.getElementById('phaser-canvas') as HTMLCanvasElement;

    const analyticsNode = await this.sdk.Scene.createNode();

    let container: HTMLElement = null;
    await this.sdk.Scene.configure(function(renderer: any) {
      container = renderer.domElement.parentElement;
      // renderer.shadowMap.enabled = false;
    });
    let scene  = [WelcomeScene, GameScene, GameOverScene];

    var config: Types.Core.GameConfig = {
      type: Phaser.CANVAS,
      parent: container,
      scene: scene,
      canvas: phaserCanvas,
      canvasStyle: 'width: 100%; height: 100%; position: absolute; pointer-events: none',
      physics: {
        default: 'arcade',
        arcade: {
          debug: false,
        },
      },
      render: {
        transparent: true,
      },
      // width: container.clientWidth * window.devicePixelRatio,
      // height: container.clientHeight * window.devicePixelRatio,
      width: window.innerWidth * window.devicePixelRatio,
      height: window.innerHeight * window.devicePixelRatio,
    };

    (window as any).getPose = this.sdk.Camera.getPose;
    this.game = new Game(config);
    this.game.sound.pauseOnBlur = false;

    const eventBus = new Events.EventEmitter();
    const gameState = MakeGameState();
    const sceneConfig: ISceneConfig = {
      game: this.game,
      eventBus,
      gameState: gameState,
      sdk: this.sdk,
      analytics: analyticsNode.analytics,
    };

    await Promise.all([
      this.sdk.Settings.update('features/measurements', false),
      this.sdk.Settings.update('features/mattertags', false),
      this.sdk.Settings.update('highlight_reel', false),
      this.sdk.Scene.register(portalSelectorSphereType, createPortalSelectorClosure(eventBus, this.sdk)),
      this.sdk.Scene.register(portalSelectType, createPortalSelectClosure(gameState, eventBus, this.sdk)),

      this.sdk.Scene.register(overlaySelectorSphereType, createOverlaySelectorClosure(eventBus, this.sdk)),
      this.sdk.Scene.register(overlaySelectType, createOverlaySelectClosure(gameState, eventBus, this.sdk)),
    ]);

    const nodes = await this.sdk.Scene.deserialize(JSON.stringify(portals));
    for (let i = 0; i < nodes.length; ++i) {
      nodes[i].start();
      nodes[i].obj3D.visible = false;
    }
    this.nodes = nodes;

    const overlay_nodes = await this.sdk.Scene.deserialize(JSON.stringify(overlays));
    for (let i = 0; i < overlay_nodes.length; ++i) {
      overlay_nodes[i].start();
    }
    this.overlay_nodes = overlay_nodes;

    /**** this is all khufu stuff
    */
    if (settings.slug === "khufu") {
      const [ lightsSceneObject ] = await this.sdk.Scene.createObjects(1);
      const lights = lightsSceneObject.addNode();
      lights.addComponent('mp.lights');
      lights.start();

      var [ sunSceneObject ] = await this.sdk.Scene.createObjects(1);
      var sunNode = sunSceneObject.addNode();
      var sunInitial = {
        enabled: true,
        color: {
          r: 1, g: 1, b: 1
        },
        intensity: 2,
        debug: false,
      };

      sunNode.addComponent('mp.directionalLight', sunInitial);
      sunNode.start();

      var [ gltfSceneObject ] = await this.sdk.Scene.createObjects(1);
      var gltf_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://tours.mused.org/assets/giza_2k.glb",
        // url: "/assets/giza_2k.glb",
        visible: true,
        localScale: {
          x: 85, y: 85, z: 85
        },
        localPosition: {
          x: -180, y: -15, z: 285
        },
        localRotation: {
          x: 0, y: 270, z: 0
        },
      };

      gltf_node.addComponent('mp.gltfLoader', initial);
      gltf_node.start();
      this.gltf_nodes = [gltf_node];

    }
    /* end khufu stuff ***/

    /**** this is all hammon stuff
    */
    if (settings.slug === "hammon") {
      const [ lightsSceneObject ] = await this.sdk.Scene.createObjects(1);
      const lights = lightsSceneObject.addNode();
      lights.addComponent('mp.lights');
      lights.start();

      var [ sunSceneObject ] = await this.sdk.Scene.createObjects(1);
      var sunNode = sunSceneObject.addNode();
      var sunInitial = {
        enabled: true,
        color: {
          r: 1, g: 1, b: 1
        },
        intensity: 2,
        debug: false,
      };

      sunNode.addComponent('mp.directionalLight', sunInitial);
      sunNode.start();

      var [ gltfSceneObject ] = await this.sdk.Scene.createObjects(1);
      var gltf_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://tours.mused.org/hammon/assets/owl_2b.glb",
        visible: true,
        localScale: {
          x: 0.1, y: 0.1, z: 0.1
        },
        localPosition: {
          x: 2.122, y: 0.749, z: 1.826
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_node.addComponent('mp.gltfLoader', initial);
      gltf_node.start();
      this.gltf_nodes = [gltf_node];

    }
    /* end hammon stuff ***/

    /**** this is all menna stuff
    */
    if (settings.slug === "menna") {
      const [ lightsSceneObject ] = await this.sdk.Scene.createObjects(1);
      const lights = lightsSceneObject.addNode();
      lights.addComponent('mp.lights');
      lights.start();

      var [ sunSceneObject ] = await this.sdk.Scene.createObjects(1);
      var sunNode = sunSceneObject.addNode();
      var sunInitial = {
        enabled: true,
        color: {
          r: 1, g: 1, b: 1
        },
        intensity: 0.7,
        debug: false,
      };

      sunNode.addComponent('mp.directionalLight', sunInitial);
      sunNode.start();

      var [ gltfSceneObject ] = await this.sdk.Scene.createObjects(1);
      var gltf_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://tours.mused.org/menna/assets/menna_example_coffin.glb",
        visible: true,
        localScale: {
          x: 0.017, y: 0.017, z: 0.017
        },
        localPosition: {
          x: 5, y: -0.2, z: 0.1
        },
        localRotation: {
          x: 0, y: 180, z: 0
        },
      };

      gltf_node.addComponent('mp.gltfLoader', initial);
      gltf_node.start();
      this.gltf_nodes = [gltf_node];

      var gltf_cat_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://tours.mused.org/menna/assets/ancient_egyptian_basalt_cat_sculpture.glb",
        visible: true,
        localScale: {
          x: 1, y: 1, z: 1
        },
        localPosition: {
          x: -0.5, y: -0.2, z: -8
        },
        localRotation: {
          x: 0, y: 90, z: 0
        },
      };
      this.gltf_nodes.push(gltf_cat_node);

      gltf_cat_node.addComponent('mp.gltfLoader', initial);
      gltf_cat_node.start();

      var gltf_chest_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://tours.mused.org/menna/assets/chest.glb",
        visible: true,
        localScale: {
          x: 0.1, y: 0.1, z: 0.1
        },
        localPosition: {
          x: 0.5, y: 0, z: -8
        },
        localRotation: {
          x: 0, y: 90, z: 0
        },
      };

      gltf_chest_node.addComponent('mp.gltfLoader', initial);
      gltf_chest_node.start();
      this.gltf_nodes.push(gltf_chest_node);

      var gltf_vessel_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://tours.mused.org/menna/assets/vessel.glb",
        visible: true,
        localScale: {
          x: 0.1, y: 0.1, z: 0.1
        },
        localPosition: {
          x: -2.5, y: 0, z: -0.4
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_vessel_node.addComponent('mp.gltfLoader', initial);
      gltf_vessel_node.start();
      this.gltf_nodes.push(gltf_vessel_node);

      var gltf_bowl_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://tours.mused.org/menna/assets/bowl.glb",
        visible: true,
        localScale: {
          x: 0.02, y: 0.02, z: 0.02
        },
        localPosition: {
          x: -3.1, y: 0.1, z: 0.3
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_bowl_node.addComponent('mp.gltfLoader', initial);
      gltf_bowl_node.start();
      this.gltf_nodes.push(gltf_bowl_node);

      var gltf_bowl2_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://tours.mused.org/menna/assets/bowl.glb",
        visible: true,
        localScale: {
          x: 0.012, y: 0.012, z: 0.012
        },
        localPosition: {
          x: -2.7, y: 0.1, z: 0.4
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_bowl2_node.addComponent('mp.gltfLoader', initial);
      gltf_bowl2_node.start();
      this.gltf_nodes.push(gltf_bowl2_node);

    }
    /* end menna stuff ***/

    /**** this is all sardis artemis stuff
    */
    if (settings.slug === "sardis_artemis") {

      const [ lightsSceneObject ] = await this.sdk.Scene.createObjects(1);
      const lights = lightsSceneObject.addNode();
      lights.addComponent('mp.lights');
      lights.start();

      var [ sunSceneObject ] = await this.sdk.Scene.createObjects(1);
      var sunNode = sunSceneObject.addNode();
      var sunInitial = {
        enabled: true,
        color: {
          r: 1, g: 1, b: 1
        },
        intensity: 2,
        debug: false,
      };

      sunNode.addComponent('mp.directionalLight', sunInitial);
      sunNode.start();

      var [ gltfSceneObject ] = await this.sdk.Scene.createObjects(1);
      var gltf_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://static.mused.org/ModelwithCapitals.glb",
        // url: "/assets/ArtemisColumns.glb",
        visible: true,
        localScale: {
          x: 1, y: 1, z: 1
        },
        localPosition: {
          x: 0, y: 0, z: 0
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_node.addComponent('mp.gltfLoader', initial);
      gltf_node.start();

      var [ gltfSceneObject2 ] = await this.sdk.Scene.createObjects(1);
      var gltf_node2 = gltfSceneObject2.addNode();
      var initial2 = {
        url: "https://static.mused.org/ModelwithCapitalsFloors.glb",
        // url: "/assets/ArtemisColumns.glb",
        visible: true,
        localScale: {
          x: 1, y: 1, z: 1
        },
        localPosition: {
          x: 0, y: 0, z: 0
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_node2.addComponent('mp.gltfLoader', initial);
      gltf_node2.start();

      var [ gltfSceneObject3 ] = await this.sdk.Scene.createObjects(1);
      var gltf_node3 = gltfSceneObject3.addNode();
      var initial3 = {
        url: "https://static.mused.org/east_side_new_without_lights.glb",
        // url: "/assets/ArtemisColumns.glb",
        visible: true,
        localScale: {
          x: 1, y: 1, z: 1
        },
        localPosition: {
          x: 0, y: 0, z: 0
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_node3.addComponent('mp.gltfLoader', initial3);
      gltf_node3.start();

      var [ gltfSceneObject4 ] = await this.sdk.Scene.createObjects(1);
      var gltf_node4 = gltfSceneObject4.addNode();
      var initial4 = {
        url: "https://static.mused.org/west_side_new_without_lights.glb",
        // url: "/assets/ArtemisColumns.glb",
        visible: true,
        localScale: {
          x: 1, y: 1, z: 1
        },
        localPosition: {
          x: 0, y: 0, z: 0
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_node4.addComponent('mp.gltfLoader', initial4);
      gltf_node4.start();

      var [ gltfSceneObject5 ] = await this.sdk.Scene.createObjects(1);
      var gltf_node5 = gltfSceneObject5.addNode();
      var initial5 = {
        url: "https://static.mused.org/temple_east_side_new.glb",
        // url: "/assets/ArtemisColumns.glb",
        visible: true,
        localScale: {
          x: 1, y: 1, z: 1
        },
        localPosition: {
          x: 0, y: 0, z: 0
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_node5.addComponent('mp.gltfLoader', initial5);
      gltf_node5.start();

      this.gltf_nodes = [gltf_node, gltf_node2, gltf_node3, gltf_node4, gltf_node5];

    }
    /* end sardis stuff ***/
    /**** this is all sardis artemis stuff
    */
    if (settings.slug === "sardis_synagogue") {

      const [ lightsSceneObject ] = await this.sdk.Scene.createObjects(1);
      const lights = lightsSceneObject.addNode();
      lights.addComponent('mp.lights');
      lights.start();

      var [ sunSceneObject ] = await this.sdk.Scene.createObjects(1);
      var sunNode = sunSceneObject.addNode();
      var sunInitial = {
        enabled: true,
        color: {
          r: 1, g: 1, b: 1
        },
        intensity: 2,
        debug: false,
      };

      sunNode.addComponent('mp.directionalLight', sunInitial);
      sunNode.start();

      var [ gltfSceneObject ] = await this.sdk.Scene.createObjects(1);
      var gltf_node = gltfSceneObject.addNode();
      var initial = {
        url: "https://static.mused.org/bath_gym_test.glb",
        visible: true,
        localScale: {
          x: 1000, y: 1000, z: 1000
        },
        localPosition: {
          x: 0, y: 0, z: 0
        },
        localRotation: {
          x: 0, y: 0, z: 0
        },
      };

      gltf_node.addComponent('mp.gltfLoader', initial);
      gltf_node.start();

      this.gltf_nodes = [gltf_node];

    }
    /* end sardis stuff ***/

    sceneConfig.eventBus.addListener(SceneEvents.SceneStart, function(payload: any) {
      this.setState({ currentScene: payload.sceneId });
    }, this);

    this.setState({ sceneConfig: sceneConfig });
    this.game.scene.start(SceneIds.Welcome, sceneConfig);

    new SceneTransitions(sceneConfig);

    this.hackFullscreen(iframeElement);
  }

  render() {
    const src = `./bundle/showcase.html?${this.queryString}&help=0&play=1&qs=1&f=0&vr=0&sp=0&cursor=0&brand=0&maxmeshq=256&maxtileq=2048&maxztileq=2048&search=0&mt=0`;
    const { currentScene, sceneConfig } = this.state;

    const renderState = () => {
      switch(currentScene) {
        case SceneIds.Welcome:
          return (<WelcomeView config={sceneConfig}></WelcomeView>);
        case SceneIds.Game:
          return (<GameView config={sceneConfig} nodes={this.nodes} paths={this.paths} overlay_nodes={this.overlay_nodes} gltf_nodes={this.gltf_nodes}></GameView>);
        case SceneIds.GameOver:
          return (<GameOverView config={sceneConfig}></GameOverView>);
      }

      return null;
    };

    return (
      <div ref={this.rootRef}>
        {renderState()}
        <Frame src={src}></Frame>
      </div>
    );
  }

  private hackFullscreen(iframeElement: HTMLIFrameElement) {
    const fsElement = iframeElement.contentDocument.querySelector('#fullscreen-mode .icon-fullscreen');
    // if there is no fullscreen button, fullscreen probably isn't supported
    if (!fsElement) return;
    fsElement.addEventListener('click', (ev) => {
      if (!this.rootRef) return;
      ev.stopImmediatePropagation();
      ev.stopPropagation();
      const rootElem = this.rootRef.current;

      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      const requestFullscreen = rootElem.requestFullscreen || rootElem.webkitRequestFullscreen || rootElem.mozRequestFullScreen || rootElem.msRequestFullscreen;
      const exitFullscreen    = document.exitFullscreen    || document.webkitExitFullscreen    || document.mozCancelFullScreen  || document.msExitFullscreen;

      if (fullscreenElement) {
        exitFullscreen.call(document);
        fsElement.classList.remove('icon-fullscreen-exit');
        fsElement.classList.add('icon-fullscreen');
      } else {
        requestFullscreen.call(rootElem);
        fsElement.classList.remove('icon-fullscreen');
        fsElement.classList.add('icon-fullscreen-exit');
      }
    }, true);
  }
}
